<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const COLLEAGUE_TAB = 0;

const store = useStore();
const { t } = useI18n();
const { agentsList } = useAgentsList(false);

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');
const teams = useMapGetter('teams/getTeams');

const activeTabIndex = ref(COLLEAGUE_TAB);
const search = ref('');
const note = ref('');
const selectedAgentIds = ref([]);
const selectedTeamId = ref(null);
const isSubmitting = ref(false);

const tabs = computed(() => [
  { label: t('CONVERSATION.LOOP_IN.TABS.COLLEAGUE') },
  { label: t('CONVERSATION.LOOP_IN.TABS.DEPARTMENT') },
]);

const isColleagueTab = computed(() => activeTabIndex.value === COLLEAGUE_TAB);

const participantIds = computed(() =>
  (
    store.getters['conversationWatchers/getByConversationId'](
      currentChat.value?.id
    ) || []
  ).map(participant => participant.id)
);

const matchesSearch = name =>
  (name || '').toLowerCase().includes(search.value.trim().toLowerCase());

// Only inbox-assignable agents can be mentioned: Messages::MentionService drops the rest.
const filteredAgents = computed(() =>
  agentsList.value.filter(
    agent => agent.id !== currentUser.value?.id && matchesSearch(agent.name)
  )
);

const filteredTeams = computed(() =>
  teams.value.filter(
    team =>
      team.id !== currentChat.value?.meta?.team?.id && matchesSearch(team.name)
  )
);

const canSubmit = computed(() =>
  isColleagueTab.value
    ? selectedAgentIds.value.length > 0
    : !!selectedTeamId.value
);

const reset = () => {
  search.value = '';
  note.value = '';
  selectedAgentIds.value = [];
  selectedTeamId.value = null;
  activeTabIndex.value = COLLEAGUE_TAB;
};

// Clear the picker when the agent moves to another conversation with the popover open.
watch(() => currentChat.value?.id, reset);

const onTabChange = tab => {
  activeTabIndex.value = tabs.value.findIndex(
    ({ label }) => label === tab.label
  );
  search.value = '';
};

const toggleAgent = agentId => {
  const index = selectedAgentIds.value.indexOf(agentId);
  if (index === -1) selectedAgentIds.value.push(agentId);
  else selectedAgentIds.value.splice(index, 1);
};

// Matches the editor's mention serialization, so the note renders as a real mention and
// Messages::MentionService adds the mentioned people as participants and notifies them.
const buildMention = (type, id, name) =>
  `[@${name}](mention://${type}/${id}/${encodeURIComponent(name)})`;

const sendPrivateNote = mentions => {
  const trimmedNote = note.value.trim();
  return store.dispatch('createPendingMessageAndSend', {
    conversationId: currentChat.value.id,
    message: trimmedNote ? `${mentions} ${trimmedNote}` : mentions,
    private: true,
    sender: currentUser.value,
  });
};

const loopInColleagues = async () => {
  const selected = agentsList.value.filter(agent =>
    selectedAgentIds.value.includes(agent.id)
  );
  const mentions = selected
    .map(agent => buildMention('user', agent.id, agent.name))
    .join(' ');

  await sendPrivateNote(mentions);
  await store.dispatch('conversationWatchers/show', {
    conversationId: currentChat.value.id,
  });
  useAlert(
    t('CONVERSATION.LOOP_IN.COLLEAGUE_SUCCESS', { count: selected.length })
  );
};

const handOverToDepartment = async () => {
  const team = teams.value.find(({ id }) => id === selectedTeamId.value);

  await sendPrivateNote(buildMention('team', team.id, team.name));
  store.dispatch('setCurrentChatTeam', {
    team,
    conversationId: currentChat.value.id,
  });
  await store.dispatch('assignTeam', {
    conversationId: currentChat.value.id,
    teamId: team.id,
  });
  useAlert(t('CONVERSATION.LOOP_IN.DEPARTMENT_SUCCESS', { team: team.name }));
};

const submit = async hide => {
  isSubmitting.value = true;
  try {
    if (isColleagueTab.value) await loopInColleagues();
    else await handOverToDepartment();
    reset();
    hide();
  } catch (error) {
    useAlert(t('CONVERSATION.LOOP_IN.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Popover @hide="reset">
    <NextButton
      v-tooltip="$t('CONVERSATION.LOOP_IN.TOOLTIP')"
      slate
      faded
      sm
      icon="i-lucide-user-plus"
      :title="$t('CONVERSATION.LOOP_IN.TOOLTIP')"
    />
    <template #content="{ hide }">
      <div class="flex flex-col gap-3 p-4 w-full md:w-[22rem]">
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-medium leading-6 text-n-slate-12">
            {{ $t('CONVERSATION.LOOP_IN.TITLE') }}
          </h3>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ $t('CONVERSATION.LOOP_IN.DESCRIPTION') }}
          </p>
        </div>

        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="onTabChange"
        />

        <Input
          v-model="search"
          size="sm"
          :placeholder="
            isColleagueTab
              ? $t('CONVERSATION.LOOP_IN.SEARCH_COLLEAGUE')
              : $t('CONVERSATION.LOOP_IN.SEARCH_DEPARTMENT')
          "
        />

        <div class="flex flex-col gap-1 max-h-56 overflow-y-auto">
          <template v-if="isColleagueTab">
            <button
              v-for="agent in filteredAgents"
              :key="agent.id"
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-n-alpha-1"
              @click="toggleAgent(agent.id)"
            >
              <Avatar
                :name="agent.name"
                :src="agent.thumbnail"
                :size="24"
                :status="agent.availability_status"
              />
              <span class="flex-1 min-w-0 truncate text-sm text-n-slate-12">
                {{ agent.name }}
              </span>
              <span
                v-if="participantIds.includes(agent.id)"
                class="text-xs text-n-slate-10"
              >
                {{ $t('CONVERSATION.LOOP_IN.ALREADY_ON_CONVERSATION') }}
              </span>
              <Icon
                v-if="selectedAgentIds.includes(agent.id)"
                icon="i-lucide-check"
                class="text-n-blue-text size-4 flex-shrink-0"
              />
            </button>
            <p
              v-if="!filteredAgents.length"
              class="px-2 py-3 mb-0 text-sm text-n-slate-10"
            >
              {{ $t('CONVERSATION.LOOP_IN.NO_COLLEAGUES') }}
            </p>
          </template>
          <template v-else>
            <button
              v-for="team in filteredTeams"
              :key="team.id"
              type="button"
              class="flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-n-alpha-1"
              @click="selectedTeamId = team.id"
            >
              <Icon
                icon="i-lucide-users"
                class="text-n-slate-11 size-4 flex-shrink-0"
              />
              <span class="flex-1 min-w-0 truncate text-sm text-n-slate-12">
                {{ team.name }}
              </span>
              <Icon
                v-if="selectedTeamId === team.id"
                icon="i-lucide-check"
                class="text-n-blue-text size-4 flex-shrink-0"
              />
            </button>
            <p
              v-if="!filteredTeams.length"
              class="px-2 py-3 mb-0 text-sm text-n-slate-10"
            >
              {{ $t('CONVERSATION.LOOP_IN.NO_DEPARTMENTS') }}
            </p>
          </template>
        </div>

        <TextArea
          v-model="note"
          :placeholder="$t('CONVERSATION.LOOP_IN.NOTE_PLACEHOLDER')"
          :max-length="1000"
          min-height="3.5rem"
          auto-height
        />

        <div class="flex items-center justify-end gap-2">
          <NextButton
            faded
            slate
            sm
            :label="$t('CONVERSATION.LOOP_IN.CANCEL')"
            @click="hide"
          />
          <NextButton
            sm
            :label="
              isColleagueTab
                ? $t('CONVERSATION.LOOP_IN.SUBMIT_COLLEAGUE')
                : $t('CONVERSATION.LOOP_IN.SUBMIT_DEPARTMENT')
            "
            :disabled="!canSubmit || isSubmitting"
            :is-loading="isSubmitting"
            @click="submit(hide)"
          />
        </div>
      </div>
    </template>
  </Popover>
</template>
